/* ========================================================================
   SITE-WIDE CONTROLS
   ======================================================================== */

/* ------------------------------------------------------------------------
   A+T Custom Select
   Progressively enhances opted-in native selects while preserving their
   values, events, and form-submission behavior as the source of truth.
   ------------------------------------------------------------------------ */
(function () {
  'use strict';

  var componentCount = 0;
  var selector = 'select[data-at-custom-select]';

  function labelFor(select) {
    var label = null;
    if (select.id) {
      Array.prototype.slice.call(document.querySelectorAll('label[for]')).some(function (candidate) {
        if (candidate.htmlFor === select.id) {
          label = candidate;
          return true;
        }
        return false;
      });
    }
    return select.getAttribute('aria-label') || (label ? label.textContent.trim() : '') || 'Select an option';
  }

  function CustomSelect(select) {
    this.select = select;
    this.originalContainer = select.parentElement;
    this.options = Array.prototype.slice.call(select.options);
    this.activeIndex = Math.max(select.selectedIndex, 0);
    this.id = 'at-custom-select-' + (++componentCount);
    this.label = labelFor(select);
    this.closeTimer = null;
    this.build();
    this.bind();
    this.syncFromNative();
    this.activate();
  }

  CustomSelect.prototype.build = function () {
    var wrapper = document.createElement('div');
    var button = document.createElement('button');
    var value = document.createElement('span');
    var caret = document.createElement('span');
    var listbox = document.createElement('ul');

    wrapper.className = 'at-custom-select';
    button.className = 'at-custom-select__button';
    button.type = 'button';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', this.id + '-listbox');
    button.setAttribute('aria-label', this.label);
    value.className = 'at-custom-select__value';
    caret.className = 'at-custom-select__caret';
    caret.setAttribute('aria-hidden', 'true');
    button.appendChild(value);
    button.appendChild(caret);

    listbox.className = 'at-custom-select__listbox';
    listbox.id = this.id + '-listbox';
    listbox.setAttribute('role', 'listbox');
    listbox.setAttribute('aria-label', this.label);
    listbox.tabIndex = -1;
    listbox.hidden = true;

    this.options.forEach(function (option, index) {
      var item = document.createElement('li');
      item.className = 'at-custom-select__option';
      item.id = this.id + '-option-' + index;
      item.setAttribute('role', 'option');
      item.setAttribute('data-index', index);
      item.setAttribute('aria-selected', 'false');
      item.textContent = option.textContent;
      if (option.disabled) item.setAttribute('aria-disabled', 'true');
      if (this.isRingSizePlaceholder(index)) item.hidden = true;
      listbox.appendChild(item);
    }, this);

    this.select.parentNode.insertBefore(wrapper, this.select);
    wrapper.appendChild(this.select);
    wrapper.appendChild(button);
    wrapper.appendChild(listbox);

    this.wrapper = wrapper;
    this.button = button;
    this.value = value;
    this.listbox = listbox;
    this.items = Array.prototype.slice.call(listbox.querySelectorAll('[role="option"]'));
  };

  // The empty Ring Size option is a closed-control placeholder, not a menu choice.
  CustomSelect.prototype.isRingSizePlaceholder = function (index) {
    return this.select.hasAttribute('data-at-ring-size-selector')
      && this.options[index]
      && this.options[index].value === '';
  };

  CustomSelect.prototype.activate = function () {
    // Native controls remain visible and usable until the enhancement is ready.
    this.select.classList.add('at-custom-select__native');
    this.select.setAttribute('tabindex', '-1');
    this.select.setAttribute('aria-hidden', 'true');
    this.originalContainer.classList.add('at-custom-select-container');
  };

  CustomSelect.prototype.bind = function () {
    this.button.addEventListener('click', this.toggle.bind(this));
    this.button.addEventListener('keydown', this.onButtonKeydown.bind(this));
    this.listbox.addEventListener('keydown', this.onListboxKeydown.bind(this));
    this.listbox.addEventListener('click', this.onOptionClick.bind(this));
    this.select.addEventListener('input', this.syncFromNative.bind(this));
    this.select.addEventListener('change', this.syncFromNative.bind(this));
    this.select.addEventListener('change', this.refreshOptions.bind(this));
    document.addEventListener('click', this.onDocumentClick.bind(this));
    document.addEventListener('keydown', this.onDocumentKeydown.bind(this));

    // Shopify normally dispatches change/input events. This small monitor also
    // keeps the display aligned when another script assigns select.value directly.
    this.valueMonitor = window.setInterval(function () {
      if (!this.select.isConnected) {
        window.clearInterval(this.valueMonitor);
        return;
      }
      if (this.lastNativeValue !== this.select.value) this.syncFromNative();
    }.bind(this), 250);
  };

  CustomSelect.prototype.refreshOptions = function () {
    this.options = Array.prototype.slice.call(this.select.options);
  };

  CustomSelect.prototype.syncFromNative = function () {
    var selectedIndex = Math.max(this.select.selectedIndex, 0);
    var selected = this.select.options[selectedIndex];
    if (!selected) return;

    this.activeIndex = selectedIndex;
    this.lastNativeValue = this.select.value;
    this.value.textContent = selected.textContent;
    this.button.setAttribute('aria-label', this.label + ': ' + selected.textContent.trim());
    this.items.forEach(function (item, index) {
      var isSelected = index === selectedIndex && !this.isRingSizePlaceholder(index);
      item.setAttribute('aria-selected', String(isSelected));
      item.classList.toggle('is-active', isSelected);
    }, this);
  };

  CustomSelect.prototype.open = function () {
    if (this.select.disabled) return;
    closeAll(this);
    this.syncFromNative();
    if (this.isRingSizePlaceholder(this.activeIndex)) {
      this.activeIndex = this.nextEnabledIndex(this.activeIndex, 1);
      this.items.forEach(function (item, index) {
        item.classList.toggle('is-active', index === this.activeIndex);
      }, this);
    }
    this.listbox.hidden = false;
    window.clearTimeout(this.closeTimer);
    this.listbox.classList.remove('is-closing');
    window.requestAnimationFrame(function () {
      if (!this.listbox.hidden) this.listbox.classList.add('is-visible');
    }.bind(this));
    this.button.setAttribute('aria-expanded', 'true');
    this.listbox.setAttribute('aria-activedescendant', this.items[this.activeIndex].id);
    this.listbox.focus();
  };

  CustomSelect.prototype.close = function (returnFocus) {
    this.button.setAttribute('aria-expanded', 'false');

    if (!this.listbox.hidden) {
      window.clearTimeout(this.closeTimer);
      this.listbox.classList.remove('is-visible');
      this.listbox.classList.add('is-closing');
      this.closeTimer = window.setTimeout(function () {
        this.listbox.hidden = true;
        this.listbox.classList.remove('is-closing');
      }.bind(this), 260);
    } else {
      this.listbox.hidden = true;
    }

    if (returnFocus) this.button.focus();
  };

  CustomSelect.prototype.toggle = function () {
    if (this.listbox.hidden) {
      this.open();
    } else {
      this.close(false);
    }
  };

  CustomSelect.prototype.nextEnabledIndex = function (start, offset) {
    var index = start;
    for (var attempts = 0; attempts < this.options.length; attempts += 1) {
      index = (index + offset + this.options.length) % this.options.length;
      if (!this.options[index].disabled && !this.isRingSizePlaceholder(index)) return index;
    }
    return start;
  };

  CustomSelect.prototype.moveActive = function (offset) {
    this.activeIndex = this.nextEnabledIndex(this.activeIndex, offset);
    this.items.forEach(function (item, index) {
      item.classList.toggle('is-active', index === this.activeIndex);
    }, this);
    this.listbox.setAttribute('aria-activedescendant', this.items[this.activeIndex].id);
    this.items[this.activeIndex].scrollIntoView({ block: 'nearest' });
  };

  CustomSelect.prototype.selectIndex = function (index, returnFocus) {
    if (this.options[index].disabled || this.isRingSizePlaceholder(index)) return;
    this.select.value = this.options[index].value;
    this.select.dispatchEvent(new Event('input', { bubbles: true }));
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
    this.syncFromNative();
    // A pointer selection closes the list beneath the cursor. Do not leave the
    // selected option in its gold/cream hover treatment while that close fade
    // completes; restore the standard blackish option treatment immediately.
    if (returnFocus === false) {
      this.items.forEach(function (item) {
        item.setAttribute('aria-selected', 'false');
        item.classList.remove('is-active');
      });
    }
    this.close(returnFocus !== false);
  };

  CustomSelect.prototype.onOptionClick = function (event) {
    var item = event.target.closest('[role="option"]');
    if (!item || item.getAttribute('aria-disabled') === 'true') return;
    // Pointer choices across the shared dropdown component finish as visually
    // closed fields. Keyboard choices continue to retain focus below.
    this.selectIndex(Number(item.getAttribute('data-index')), false);
  };

  CustomSelect.prototype.onButtonKeydown = function (event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.open();
      this.moveActive(event.key === 'ArrowDown' ? 1 : -1);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggle();
    } else if (event.key === 'Escape') {
      this.close(false);
    }
  };

  CustomSelect.prototype.onListboxKeydown = function (event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveActive(event.key === 'ArrowDown' ? 1 : -1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.activeIndex = this.nextEnabledIndex(-1, 1);
      this.moveActive(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.activeIndex = this.nextEnabledIndex(0, -1);
      this.moveActive(0);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.selectIndex(this.activeIndex);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.close(true);
    } else if (event.key === 'Tab') {
      this.close(false);
    }
  };

  CustomSelect.prototype.onDocumentClick = function (event) {
    if (!this.wrapper.contains(event.target)) this.close(false);
  };

  CustomSelect.prototype.onDocumentKeydown = function (event) {
    if (event.key === 'Escape' && !this.listbox.hidden) this.close(true);
  };

  function closeAll(except) {
    document.querySelectorAll('.at-custom-select').forEach(function (wrapper) {
      var instance = wrapper.__atCustomSelect;
      if (instance && instance !== except) instance.close(false);
    });
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(selector).forEach(function (select) {
      if (select.dataset.atCustomSelectInitialized === 'true') return;
      try {
        var instance = new CustomSelect(select);
        select.dataset.atCustomSelectInitialized = 'true';
        instance.wrapper.__atCustomSelect = instance;
      } catch (error) {
        // Preserve the native control as the usable fallback if enhancement fails.
        select.classList.remove('at-custom-select__native');
        select.removeAttribute('tabindex');
        select.removeAttribute('aria-hidden');
        if (select.parentElement) select.parentElement.classList.remove('at-custom-select-container');
      }
    });
  }

  window.ATCustomSelect = { init: init };

  document.addEventListener('DOMContentLoaded', function () {
    init(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('at:filtering-rendered', function (event) {
    init(event.target || document);
  });
}());
